import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Crown } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import PasswordStrengthInput from "../shared/PasswordStrengthInput";
import { ProBadge } from "../shared/ProBadge";
import { validatePassword } from "../../lib/passwordRules";
import api from "../../lib/apiClient";
import { cacheInvalidate } from "../../lib/cache";

/**
 * ProfileTab — Bio, Sponsor links, Channel-Chat retention controls.
 * Owns its own form state; saves through PUT /api/users/profile.
 */
const ProfileTab = ({ user, token, refreshUser }) => {
  const [bio, setBio] = useState(user?.bio || "");
  const [affiliateLink1Title, setAffiliateLink1Title] = useState("");
  const [affiliateLink1Url, setAffiliateLink1Url] = useState("");
  const [affiliateLink2Title, setAffiliateLink2Title] = useState("");
  const [affiliateLink2Url, setAffiliateLink2Url] = useState("");
  // Slots 3 + 4 are Producer-only (Jun 18, 2026). They render
  // behind a tier check and the backend trims the array to 2 for
  // non-Producers.
  const [affiliateLink3Title, setAffiliateLink3Title] = useState("");
  const [affiliateLink3Url, setAffiliateLink3Url] = useState("");
  const [affiliateLink4Title, setAffiliateLink4Title] = useState("");
  const [affiliateLink4Url, setAffiliateLink4Url] = useState("");
  const isProducer = (user?.subscription_tier || "").toLowerCase() === "producer";
  const [channelChatTtl, setChannelChatTtl] = useState(20);
  const [saving, setSaving] = useState(false);

  // Hydrate from user object
  useEffect(() => {
    if (user?.bio !== undefined) setBio(user.bio || "");
    if (user?.affiliate_links) {
      const links = user.affiliate_links;
      const hydrate = (i, setT, setU) => {
        if (!links[i]) return;
        if (typeof links[i] === "object") {
          setT(links[i].title || ""); setU(links[i].url || "");
        } else { setU(links[i]); }
      };
      hydrate(0, setAffiliateLink1Title, setAffiliateLink1Url);
      hydrate(1, setAffiliateLink2Title, setAffiliateLink2Url);
      hydrate(2, setAffiliateLink3Title, setAffiliateLink3Url);
      hydrate(3, setAffiliateLink4Title, setAffiliateLink4Url);
    }
    if (user?.channel_chat_ttl_days) setChannelChatTtl(user.channel_chat_ttl_days);
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const links = [];
      const push = (title, url) => {
        if (url.trim()) links.push({ title: title.trim() || url.trim(), url: url.trim() });
      };
      push(affiliateLink1Title, affiliateLink1Url);
      push(affiliateLink2Title, affiliateLink2Url);
      if (isProducer) {
        push(affiliateLink3Title, affiliateLink3Url);
        push(affiliateLink4Title, affiliateLink4Url);
      }
      const ttl = parseInt(channelChatTtl, 10);
      if (Number.isNaN(ttl) || ttl < 1 || ttl > 365) {
        toast.error("Channel Chat retention must be between 1 and 365 days");
        setSaving(false);
        return;
      }
      await api.put(`/users/profile`, {
        bio,
        affiliate_links: links,
        channel_chat_ttl_days: ttl,
      });
      // Bust the public-profile SWR cache so navigating to /:username
      // (or refreshing it in the same tab) shows the new bio / sponsor
      // links / chat TTL immediately instead of the prior 30-second
      // snapshot. Mirrors what useRecorderPublish does after upload.
      // Without this, users see "I saved and the public profile didn't
      // change" — recurrence pattern that hit Eric on Jun 15, 2026.
      if (user?.username) {
        cacheInvalidate(`profile:${user.username.toLowerCase()}`);
        cacheInvalidate(`channelChat:${user.username.toLowerCase()}`);
      }
      toast.success("Profile updated!");
      await refreshUser?.();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        toast.error(errorDetail[0]?.msg || "Failed to update profile");
      } else {
        toast.error(errorDetail || "Failed to update profile");
      }
    }
    setSaving(false);
  };

  return (
    <Card className="bg-gray-900 border-gray-800">
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="text-white text-base sm:text-lg">Edit Profile</CardTitle>
        <CardDescription className="text-gray-400 text-xs sm:text-sm">
          Your public profile: globaleyes.tv/{user.username}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
        <div>
          <Label className="text-gray-300 text-sm">Bio</Label>
          <Textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white mt-1"
            placeholder="Tell the world about yourself..."
            rows={3}
            data-testid="bio-input"
          />
        </div>
        <div>
          <Label className="text-gray-300 text-sm">Sponsor Link 1</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
            <Input value={affiliateLink1Title} onChange={(e) => setAffiliateLink1Title(e.target.value)}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="Display Title (e.g., My Ancestry)"
              data-testid="affiliate-link-1-title" />
            <Input value={affiliateLink1Url} onChange={(e) => setAffiliateLink1Url(e.target.value)}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="https://example.com/affiliate"
              data-testid="affiliate-link-1-url" />
          </div>
        </div>
        <div>
          <Label className="text-gray-300 text-sm">Sponsor Link 2</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
            <Input value={affiliateLink2Title} onChange={(e) => setAffiliateLink2Title(e.target.value)}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="Display Title (e.g., My Shop)"
              data-testid="affiliate-link-2-title" />
            <Input value={affiliateLink2Url} onChange={(e) => setAffiliateLink2Url(e.target.value)}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="https://example.com/affiliate"
              data-testid="affiliate-link-2-url" />
          </div>
        </div>
        {isProducer ? (
          <>
            <div data-testid="affiliate-link-3-row">
              <Label className="text-gray-300 text-sm flex items-center gap-2">
                Sponsor Link 3
                <ProBadge unlocked />
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                <Input value={affiliateLink3Title} onChange={(e) => setAffiliateLink3Title(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Display Title"
                  data-testid="affiliate-link-3-title" />
                <Input value={affiliateLink3Url} onChange={(e) => setAffiliateLink3Url(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="https://example.com/affiliate"
                  data-testid="affiliate-link-3-url" />
              </div>
            </div>
            <div data-testid="affiliate-link-4-row">
              <Label className="text-gray-300 text-sm flex items-center gap-2">
                Sponsor Link 4
                <ProBadge unlocked />
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                <Input value={affiliateLink4Title} onChange={(e) => setAffiliateLink4Title(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Display Title"
                  data-testid="affiliate-link-4-title" />
                <Input value={affiliateLink4Url} onChange={(e) => setAffiliateLink4Url(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="https://example.com/affiliate"
                  data-testid="affiliate-link-4-url" />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Iter-76: show slots 3 & 4 as visibly locked rather than hiding
                them behind a generic upsell card. The crown badge + greyed
                inputs makes the upgrade incentive show up at the exact
                moment of intent (when a user is editing their sponsor links). */}
            {[3, 4].map((n) => (
              <div key={n} data-testid={`affiliate-link-${n}-row-locked`} className="opacity-70">
                <Label className="text-gray-400 text-sm flex items-center gap-2">
                  Sponsor Link {n}
                  <ProBadge />
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                  <Input disabled value="" placeholder="Producer-only slot"
                    className="bg-gray-900/60 border-gray-800 text-gray-500 cursor-not-allowed" />
                  <Input disabled value="" placeholder="Upgrade to unlock"
                    className="bg-gray-900/60 border-gray-800 text-gray-500 cursor-not-allowed" />
                </div>
              </div>
            ))}
            <Link
              to="/pricing"
              className="block bg-amber-950/30 hover:bg-amber-900/40 border border-amber-800/40 hover:border-amber-600/60 rounded-lg p-3 text-xs text-amber-200 transition-colors"
              data-testid="affiliate-link-upsell"
            >
              <span className="font-semibold inline-flex items-center gap-1">
                <Crown className="h-3 w-3" /> Unlock 2 more sponsor links →
              </span>{" "}
              Producers display 4 sponsor links on their public profile (vs. 2 on lower tiers), plus 9-minute videos.
            </Link>
          </>
        )}
        <div>
          <Label className="text-gray-300 text-sm">Channel Chat retention</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input type="number" min={1} max={365} value={channelChatTtl}
              onChange={(e) => setChannelChatTtl(e.target.value)}
              className="bg-gray-800 border-gray-700 text-white w-28"
              data-testid="channel-chat-ttl-input" />
            <span className="text-gray-400 text-sm">days before messages disappear</span>
          </div>
          <p className="text-gray-500 text-xs mt-1">
            Shown next to "Channel Chat" on your public profile. Default: 20 days. Range: 1–365.
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
          data-testid="save-profile-btn">
          {saving ? "Saving..." : "Save Changes"}
        </Button>
        <ChangePasswordSection token={token} />
      </CardContent>
    </Card>
  );
};

// Separated from the main profile form because password changes have
// their own backend endpoint, their own validation rules, and shouldn't
// be tangled with the autosaving bio/affiliate-link state. Local state
// only — never leaks the values upward.
const ChangePasswordSection = ({ token }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const pwError = validatePassword(next);
    if (pwError) {
      toast.error(pwError);
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/auth/change-password`,
        { current_password: current, new_password: next }
      );
      toast.success("Password updated. Use the new password next time you log in.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    }
    setSaving(false);
  };

  return (
    <div className="pt-6 mt-6 border-t border-gray-800" data-testid="change-password-section">
      <h3 className="text-white text-sm font-medium mb-1">Change password</h3>
      <p className="text-gray-500 text-xs mb-3">
        We'll ask for your current password as a second factor. Minimum 14 characters with a mix of upper, lower, number, and symbol.
      </p>
      <div className="space-y-2 max-w-md">
        <Input
          type="password" autoComplete="current-password"
          value={current} onChange={(e) => setCurrent(e.target.value)}
          className="bg-gray-800 border-gray-700 text-white"
          placeholder="Current password"
          data-testid="cp-current-input"
        />
        <PasswordStrengthInput
          value={next}
          onChange={setNext}
          placeholder="New password"
          testIdBase="cp-new"
        />
        <Input
          type="password" autoComplete="new-password"
          value={confirm} onChange={(e) => setConfirm(e.target.value)}
          className="bg-gray-800 border-gray-700 text-white"
          placeholder="Confirm new password"
          data-testid="cp-confirm-input"
        />
        <Button
          onClick={submit}
          disabled={saving || !current || !next || !confirm}
          className="bg-sky-600 hover:bg-sky-700 w-full sm:w-auto"
          data-testid="cp-submit-btn"
        >
          {saving ? "Updating..." : "Change password"}
        </Button>
      </div>
    </div>
  );
};

export default ProfileTab;
